import React, { useEffect, useRef, useState } from "react";
import {
  FileText,
  Megaphone,
  MessageSquare,
  Search,
  Phone,
  GraduationCap,
  UserCheck,
  BookOpen,
  ArrowRight,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const iconMap = {
  "file-text": FileText,
  megaphone: Megaphone,
  "message-square": MessageSquare,
  search: Search,
  phone: Phone,
  "graduation-cap": GraduationCap,
};

const RecruitmentSection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const principles = t("recruitment.principles") || [];

  return (
    <section id="recruitment" ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`mb-12 max-w-4xl transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#E8792B]/10 rounded-xl mb-6">
            <UserCheck className="w-7 h-7 text-[#E8792B]" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] mb-4">
            {t("recruitment.title")}
          </h2>
          <div className="w-20 h-1 bg-[#E8792B] rounded-full mb-6" />
          <p className="text-gray-600 text-base leading-relaxed">{t("recruitment.intro")}</p>
        </div>

        <h3 className="text-xl font-bold text-[#1a1a2e] mb-6">
          {t("recruitment.principlesTitle")}
        </h3>

        {/* GXR-style cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 mb-16">
          {Array.isArray(principles) &&
            principles.map((p, index) => {
              const Icon = iconMap[p.icon] || FileText;
              const num = String(index + 1).padStart(2, "0");
              return (
                <div
                  key={index}
                  className={`group relative overflow-hidden p-8 lg:p-10 bg-white border border-gray-200 hover:border-[#E8792B]/50 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-500 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Large faded number watermark */}
                  <div className="absolute -bottom-8 -right-2 font-black text-[160px] lg:text-[200px] leading-none text-[#E8792B]/[0.07] pointer-events-none select-none">
                    {num}
                  </div>

                  {/* Top hairline accent */}
                  <span className="absolute top-0 left-0 h-px w-12 bg-[#E8792B] transition-all duration-500 group-hover:w-full" />

                  {/* Square icon tile */}
                  <div className="relative inline-flex items-center justify-center h-12 w-12 border border-[#E8792B]/40 bg-[#E8792B]/5 transition-all duration-300 group-hover:bg-[#E8792B]/10 group-hover:border-[#E8792B] group-hover:shadow-[0_0_24px_rgba(232,121,43,0.25)]">
                    <Icon className="h-5 w-5 text-[#E8792B]" strokeWidth={2} />
                  </div>

                  <div className="relative mt-8 text-[11px] font-bold tracking-[0.3em] text-[#E8792B]/70">
                    · {num} ·
                  </div>

                  <h3 className="relative mt-4 text-[20px] lg:text-[24px] font-bold leading-[1.2] text-[#1a1a2e] tracking-tight">
                    {p.title}
                  </h3>

                  <p className="relative mt-5 text-gray-600 text-[14px] leading-relaxed max-w-[36ch]">
                    {p.text}
                  </p>

                  {/* Bottom arrow reveal */}
                  <div className="relative mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-transparent group-hover:text-[#E8792B] transition-all duration-300">
                    <span className="h-px w-6 bg-[#E8792B] transition-all duration-300 group-hover:w-10" />
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              );
            })}
        </div>

        {/* Training & Education sub-section (unchanged) */}
        <div
          className={`bg-[#1a1a2e] rounded-2xl p-8 lg:p-12 text-white transition-all duration-700 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 bg-[#E8792B] rounded-xl flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-3">{t("recruitment.trainingTitle")}</h3>
              <p className="text-white/80 text-base leading-relaxed">
                {t("recruitment.trainingText")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecruitmentSection;
